import { DownOutlined, LinkOutlined, RightOutlined } from '@ant-design/icons';
import {
  Alert,
  message as antdMessage,
  Button,
  Form,
  Input,
  Modal,
  Typography,
} from 'antd';
import { useContext, useEffect, useRef, useState } from 'react';
import { UserContext } from '../../UserContext';
import { CrmType, crmTypeMeta, sharedFields } from '../../types/crmTypes';
import { FubMfaCode } from './fub-mfa-code';
import './crm-credentials-editor.css';

// Backend-accepted field keys. These are the exact camelCase names the
// /api/clients/{client_config_id}/crm/credentials endpoint accepts.
type BackendFieldName =
  | 'apiKey'
  | 'username'
  | 'accountPassword'
  | 'totpSecret'
  | 'recoveryCode'
  | 'authCookie'
  | 'email'
  | 'siteName'
  | 'subdomain';

interface FieldSpec {
  name: BackendFieldName;
  label: string;
  secret: boolean;
  // FUB-only: updating this field hits the slow crmSession verification path.
  triggersFubSlowPath?: boolean;
  // Optional contextual help shown next to the field label.
  tooltip?: string;
}

// Fields that use password input (Input.Password) in the credential editor.
const SECRET_FIELDS: ReadonlySet<string> = new Set([
  'apiKey',
  'accountPassword',
  'totpSecret',
  'recoveryCode',
  'authCookie',
]);

function getFieldSpecs(
  crmType: CrmType,
  hasWebLogin: boolean,
): FieldSpec[] | null {
  const meta = crmTypeMeta[crmType];
  if (!meta.credentialFields) {
    return null;
  }

  let specs: FieldSpec[] = meta.credentialFields.map(key => {
    const field = sharedFields[key];
    return {
      name: field.name as BackendFieldName,
      label: field.label,
      secret: SECRET_FIELDS.has(field.name),
    };
  });

  // CRM-specific adjustments that can't be expressed in metadata alone.
  switch (crmType) {
    case 'fub':
      // The backend enables web login once all three are saved together.
      if (!hasWebLogin) {
        specs = specs
          .filter(s => s.name !== 'authCookie')
          .map(s =>
            s.name === 'username' ||
            s.name === 'accountPassword' ||
            s.name === 'totpSecret'
              ? {
                  ...s,
                  tooltip:
                    'Web login is off for this client. Save username, password, and authenticator setup key together to enable it.',
                }
              : s,
          );
      } else {
        specs = specs.map(s =>
          s.name === 'username' || s.name === 'accountPassword'
            ? { ...s, triggersFubSlowPath: true }
            : s,
        );
      }
      break;
    case 'salesforce':
      // Salesforce's `apiKey` input maps to the security token — label it
      // as such in the UI even though the backend key stays `apiKey`.
      specs = specs.map(s =>
        s.name === 'apiKey'
          ? {
              ...s,
              label: 'Security Token',
              tooltip:
                'Found in Salesforce under Settings → My Personal Information → Reset My Security Token. Required alongside the password for API access.',
            }
          : s,
      );
      break;
  }

  return specs;
}

interface SuccessResponse {
  status: 'success';
  client_config_id: string;
  crm_type: string;
  updated_fields: string[];
  verification_scope: 'full' | 'api_key_only' | 'login_only' | 'none';
  web_login_enabled?: boolean;
  verified_at?: string;
  message: string;
}

type FormValues = Partial<Record<BackendFieldName, string>>;

interface CrmCredentialsEditorProps {
  apiUrl: string;
  accessToken: string;
  clientConfigId: string;
  crmType: CrmType | undefined;
  hasWebLogin: boolean;
  crmCredentials: Record<string, string>;
}

export function CrmCredentialsEditor(props: CrmCredentialsEditorProps) {
  const user = useContext(UserContext);
  return (
    <CredentialEditor
      key={`${props.apiUrl}:${props.clientConfigId}:${user?.userID}:${Boolean(props.accessToken)}:${props.crmType}:${props.hasWebLogin}`}
      {...props}
    />
  );
}

function CredentialEditor(props: CrmCredentialsEditorProps) {
  const [form] = Form.useForm<FormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [success, setSuccess] = useState<SuccessResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mfaRevision, setMfaRevision] = useState(0);
  const active = useRef(true);
  const pendingSave = useRef<AbortController | null>(null);

  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
      pendingSave.current?.abort();
    };
  }, []);

  const fieldSpecs = props.crmType
    ? getFieldSpecs(props.crmType, props.hasWebLogin)
    : null;

  // Map of backend field name → human-readable label, used to render the
  // success banner's "Updated fields" list with friendly names.
  const labelByName = fieldSpecs
    ? new Map<string, string>(fieldSpecs.map(spec => [spec.name, spec.label]))
    : new Map<string, string>();

  const submit = async (values: FormValues) => {
    if (!fieldSpecs || !active.current) {
      return;
    }

    // Only include fields the user actually edited. Partial updates are
    // intentional — the backend merges untouched fields from Key Vault.
    const touched: Record<string, string> = {};
    for (const spec of fieldSpecs) {
      if (!form.isFieldTouched(spec.name)) {
        continue;
      }
      const value = values[spec.name];
      if (typeof value === 'string' && value.length > 0) {
        touched[spec.name] = value;
      }
    }

    if (Object.keys(touched).length === 0) {
      void antdMessage.warning('No credential fields were modified.');
      return;
    }

    // FUB slow path: warn before submitting and use a long client timeout.
    const willHitFubSlowPath =
      props.crmType === 'fub' &&
      fieldSpecs.some(spec => spec.triggersFubSlowPath && spec.name in touched);

    if (willHitFubSlowPath) {
      const confirmed = await new Promise<boolean>(resolve => {
        Modal.confirm({
          title: 'Verifying FUB login credentials',
          content:
            'Verifying FUB login credentials can take up to 2 minutes. Please keep this tab open until it completes.',
          okText: 'Continue',
          cancelText: 'Cancel',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
      if (!confirmed || !active.current) {
        return;
      }
    }

    setSubmitting(true);
    setSuccess(null);
    setError(null);
    if ('totpSecret' in touched) {
      setMfaRevision(value => value + 1);
    }

    // Backend budget for FUB web-login verification is ~90s; give it
    // generous headroom on the client. Other CRMs resolve quickly.
    const timeoutMs = willHitFubSlowPath ? 130_000 : 30_000;
    const controller = new AbortController();
    pendingSave.current = controller;
    const timeoutHandle = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(
        `${props.apiUrl}/api/clients/${props.clientConfigId}/crm/credentials`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${props.accessToken}`,
          },
          body: JSON.stringify({ fields: touched }),
          signal: controller.signal,
        },
      );

      const body: unknown = await response.json().catch(() => null);
      if (!active.current) {
        return;
      }

      if (
        response.ok &&
        body &&
        typeof body === 'object' &&
        (body as { status?: string }).status === 'success'
      ) {
        const successBody = body as SuccessResponse;
        setSuccess(successBody);
        const validNames = new Set<string>(fieldSpecs.map(spec => spec.name));
        const namesToReset = successBody.updated_fields.filter(
          (name): name is BackendFieldName => validNames.has(name),
        );
        form.resetFields(namesToReset);
        return;
      }

      const errorBody = body as {
        error_detail?: string;
        error_category?: string;
      } | null;
      setError(
        errorBody?.error_detail ??
          errorBody?.error_category ??
          `Request failed (HTTP ${response.status}).`,
      );
    } catch (err) {
      if (!active.current) {
        return;
      }
      if (err instanceof DOMException && err.name === 'AbortError') {
        setError(
          'Request timed out before the CRM responded. Please retry in a few minutes.',
        );
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      clearTimeout(timeoutHandle);
      if (active.current) {
        setSubmitting(false);
      }
    }
  };

  const keyVaultEntries = Object.entries(props.crmCredentials);
  const hasKeyVaultLinks = keyVaultEntries.some(
    ([, value]) => value !== 'No key',
  );
  const fieldGroups = [
    {
      title: 'Login & API access',
      fields: fieldSpecs?.filter(
        spec => spec.name !== 'totpSecret' && spec.name !== 'recoveryCode',
      ),
    },
    {
      title: 'MFA setup',
      fields: fieldSpecs?.filter(
        spec => spec.name === 'totpSecret' || spec.name === 'recoveryCode',
      ),
    },
  ];

  return (
    <div className="crm-credentials">
      {hasKeyVaultLinks ? (
        <div className="crm-credentials-links">
          {keyVaultEntries.map(([key, value]) => (
            <div key={key}>
              {value === 'No key' ? (
                <Typography.Text type="secondary">
                  {key}: not set
                </Typography.Text>
              ) : (
                <a href={value} rel="noopener noreferrer" target="_blank">
                  <LinkOutlined style={{ marginRight: 6, fontSize: 12 }} />
                  {key}
                </a>
              )}
            </div>
          ))}
        </div>
      ) : (
        <Typography.Text type="secondary">
          No credentials found.
        </Typography.Text>
      )}

      {/* --- Lofty re-authorization (OAuth, no editable credentials) --- */}
      {props.crmType === 'lofty' && (
        <div style={{ marginTop: 12 }}>
          <Button
            href={`/lofty/${props.clientConfigId}`}
            icon={<LinkOutlined />}
            rel="noopener noreferrer"
            size="small"
            target="_blank"
          >
            Re-authorize Lofty (OAuth)
          </Button>
        </div>
      )}

      {props.crmType === 'fub' && props.accessToken && !submitting && (
        <FubMfaCode
          accessToken={props.accessToken}
          apiUrl={props.apiUrl}
          clientConfigId={props.clientConfigId}
          key={mfaRevision}
        />
      )}

      {fieldSpecs && (
        <div style={{ marginTop: 12 }}>
          <Button
            aria-expanded={editing}
            icon={editing ? <DownOutlined /> : <RightOutlined />}
            onClick={() => {
              if (editing) {
                form.resetFields(['totpSecret', 'recoveryCode', 'authCookie']);
              }
              setEditing(prev => !prev);
            }}
            size="small"
          >
            Edit Credentials
          </Button>

          {editing && (
            <div className="crm-credentials-form">
              {success && (
                <Alert
                  closable
                  description={
                    <div>
                      <div>{success.message}</div>
                      {success.updated_fields.length > 0 && (
                        <div style={{ marginTop: 8 }}>
                          <Typography.Text strong>
                            Updated fields:{' '}
                          </Typography.Text>
                          {success.updated_fields
                            .map(name => labelByName.get(name) ?? name)
                            .join(', ')}
                        </div>
                      )}
                      {success.web_login_enabled && (
                        <div style={{ marginTop: 8 }}>
                          <Typography.Text type="secondary">
                            Web login is now enabled for this client.
                          </Typography.Text>
                        </div>
                      )}
                      {success.verification_scope === 'api_key_only' && (
                        <div style={{ marginTop: 8 }}>
                          <Typography.Text type="secondary">
                            Note: some fields were saved but could not be
                            independently verified against the CRM. They&apos;ll
                            be exercised on the next scheduled sync.
                          </Typography.Text>
                        </div>
                      )}
                    </div>
                  }
                  message="Credentials saved"
                  onClose={() => setSuccess(null)}
                  showIcon
                  style={{ marginBottom: 16 }}
                  type="success"
                />
              )}
              {error && (
                <Alert
                  closable
                  description={error}
                  message="Error"
                  onClose={() => setError(null)}
                  showIcon
                  style={{ marginBottom: 16 }}
                  type="error"
                />
              )}
              <Form<FormValues>
                component={false}
                disabled={submitting}
                form={form}
                layout="vertical"
                onFinish={values => {
                  void submit(values);
                }}
              >
                {fieldGroups
                  .filter(group => group.fields?.length)
                  .map(group => (
                    <section
                      className="crm-credentials-group"
                      key={group.title}
                    >
                      <Typography.Text strong>{group.title}</Typography.Text>
                      <div className="crm-credentials-grid">
                        {group.fields?.map(spec => (
                          <Form.Item
                            className={
                              spec.name === 'apiKey' ||
                              spec.name === 'authCookie'
                                ? 'crm-credentials-api-key'
                                : undefined
                            }
                            key={spec.name}
                            label={spec.label}
                            labelCol={{ span: 24 }}
                            name={spec.name}
                            rules={[
                              {
                                whitespace: true,
                                message: `${spec.label} cannot be blank.`,
                              },
                            ]}
                            tooltip={spec.tooltip}
                            wrapperCol={{ span: 24 }}
                          >
                            {spec.secret ? (
                              <Input.Password
                                autoComplete="new-password"
                                placeholder={`Enter new ${spec.label.toLowerCase()}`}
                              />
                            ) : (
                              <Input
                                placeholder={`Enter new ${spec.label.toLowerCase()}`}
                              />
                            )}
                          </Form.Item>
                        ))}
                      </div>
                    </section>
                  ))}
                <div className="crm-credentials-footer">
                  <Typography.Text type="secondary">
                    Only fields you fill in will be updated.
                  </Typography.Text>
                  <Button
                    loading={submitting}
                    onClick={() => form.submit()}
                    type="primary"
                  >
                    Save Credentials
                  </Button>
                </div>
              </Form>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
